// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROJECT — entry + revisions list (lot B.10 — découpage du monstre)
// Migration S5.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Découpage en 4 sous-fichiers (mai 2026) :
//   - index.ts (ici)  : `ps` state, pages.* entries, renderProjectRevisionsPage,
//                       showStartRevisionModal, showEditRevisionModal,
//                       _confirmLeave, etc. Side-effect imports les 3 autres.
//   - risk-table.ts   : renderProjectPage + rendu table risques + helpers.
//   - handlers.ts     : pickers d'action + event handlers + saveUpdate.
//   - risk-modal.ts   : showRiskHistory + chart + showAddRiskModal.
//
// `ps` est shimé sur window à la fin d'index.ts — les autres sous-fichiers
// y accèdent en bare ref (résolu via global object).

import { definePage, navigateTo } from '../../router.js';
import { markLastModified } from '../../utils/last-modified-anim.js';
import { openProjectPilotPicker } from '../../utils/pilot-picker.js';

import './risk-table.js';
import './handlers.js';
import './risk-modal.js';

// Les types globaux de la page (PageProject, PageRisk, PageAction, PageEntry,
// ProjectStateLike enrichi, bare-refs cross-fichiers) vivent dans
// app/static/js/types/page-project.d.ts.

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROJECT (risk update entry)
// ═══════════════════════════════════════════════════════════════════════════

const ps: ProjectStateLike = {
  project: null,
  risks: [],
  users: [],
  stages: [],
  actionStatuses: [],
  entries: {},
  prevEntries: {},
  updateDate: todayStr(),
  selectedStage: null,
  sortBy: 'severity',
  sortDir: 'desc',
  stableOrder: [],
  expandedRiskId: null,
  visibilityFilter: 'all',
  showNonRelevant: true,
};

// RefactoUX mai 2026 : `pages.project` était une simple redirection vers la
// page des révisions. Devient maintenant la page d'édition complète d'un
// projet (3 blocs côte à côte : Général · Pilotes+Programmes · Comités) +
// un bloc "Risques" en pleine largeur (lien Évolution, CTA Démarrer une
// révision, table des révisions). Remplace l'ancienne popup `showProjectModal`
// pour l'édition (la création ad-hoc d'un projet garde la modale pour rester
// rapide depuis le dashboard).
pages.project = definePage<'project/:id'>(async ({ id }) => {
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [project, stages, riskLists, users, committees, programs, revisions] = (await Promise.all(
      [
        api.getProject(id),
        loadStages(),
        // Repli identique à celui de la liste des projets : un 403 sur
        // `/risk-lists` (utilisateur sans `risk_lists.read`) ne doit pas
        // emporter la fiche projet entière, dont les six autres appels ont
        // réussi. Cf. `getRevisions()` deux lignes plus bas, même motif.
        loadRiskLists().catch(() => []),
        api.getUsers(),
        api.getCommittees(),
        api.getPrograms(),
        api.getRevisions(id).catch(() => [] as PageRevision[]),
      ],
    )) as [
      PageProject,
      Array<{ id: number; name: string }>,
      Array<{ id: number; name: string }>,
      Array<{ id: number; name: string; trigram?: string; is_team?: boolean }>,
      Array<{ id: number; code: string; name: string }>,
      Array<{ id: number; code?: string | null; name: string; projects?: Array<{ id: number }> }>,
      PageRevision[],
    ];
    renderProjectEditPage(id, project, stages, riskLists, users, committees, programs, revisions);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

// RefactoUX mai 2026 : la page #project/:id/revisions a été fusionnée dans
// #project/:id (page édition complète, table des révisions dans le bloc
// Risques). On garde la route pour ne pas casser les breadcrumbs externes
// (page-evolution, page-bascule, page-matrix, page-projects, etc.) — simple
// redirection vers la nouvelle page consolidée.
pages.projectRevisions = definePage<'project/:id/revisions'>(({ id }) => {
  navigateTo('project/:id', { id });
});

function renderProjectRevisionsPage(
  pid: number,
  project: PageProject,
  revisions: PageRevision[],
): void {
  setHeader(
    `<a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('pageProject.breadcrumb.projects'))}</a> / ${esc(project.name)}`,
    `<a href="#evolution/${pid}" class="btn btn-secondary ml-auto" title="${esc(t('pageProject.btn.evolutionTitle'))}">${esc(t('pageProject.btn.evolution'))}</a>` +
      `<button class="btn btn-primary" id="btn-start-revision" style="margin-left:8px">${esc(t('pageProject.btn.startRevision'))}</button>`,
  );

  // RefactoUX/Actions mai 2026 : liste unique (révision en cours + clôturées)
  // triable par colonne. Tri par défaut "date début descendante" — la révision
  // active a logiquement la date la plus récente et apparaît en haut.
  //
  // Comportement :
  //   - clic sur le nom → consultation (navigation #revision/:id)
  //   - clic sur l'œil → idem (alternative explicite, sur lignes clôturées)
  //   - boutons Matrice / Clore / ✎ uniquement sur la révision en cours
  //   - clôturées : œil seul (pas de pencil ni close)
  let sortKey: 'name' | 'status' | 'date_start' | 'date_end' = 'date_start';
  let sortDir: 'asc' | 'desc' = 'desc';

  const arrowFor = (key: string): string =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const sortedRevisions = (): PageRevision[] => {
    return [...revisions].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = (a.name || '').localeCompare(b.name || '');
      } else if (sortKey === 'status') {
        // false (en cours) < true (clôturée) → ASC = en cours d'abord.
        cmp = Number(!!a.is_closed) - Number(!!b.is_closed);
      } else if (sortKey === 'date_start') {
        cmp = (a.date_start || '').localeCompare(b.date_start || '');
      } else if (sortKey === 'date_end') {
        // Sentinelle : les révisions en cours (sans date_end) sont triées
        // "après le futur lointain" en ASC → elles flottent en fin/début
        // selon la direction. Cohérent visuellement.
        const ae = a.date_end || (a.is_closed ? '' : '￿');
        const be = b.date_end || (b.is_closed ? '' : '￿');
        cmp = ae.localeCompare(be);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  const renderTable = (): string => {
    if (revisions.length === 0) {
      return `<div class="card" style="background:var(--bg3);text-align:center;padding:32px 16px">
        <div style="font-size:14px;color:var(--text3);margin-bottom:12px">${esc(t('pageProject.noRevision'))}</div>
        <button class="btn btn-primary" id="btn-start-revision-2">${esc(t('pageProject.btn.startRevision'))}</button>
      </div>`;
    }
    const rows = sortedRevisions()
      .map((r) => {
        const isClosed = !!r.is_closed;
        const statusLabel = isClosed
          ? `<span style="display:inline-block;padding:2px 8px;background:var(--bg3);color:var(--text2);border-radius:8px;font-size:11px">${esc(t('pageProject.rev.closed'))}</span>`
          : `<span style="display:inline-block;padding:2px 8px;background:var(--accent-bg);color:var(--accent);border-radius:8px;font-size:11px;font-weight:600">${esc(t('pageProject.history.inProgress'))}</span>`;
        // RefactoUX mai 2026 : "Consulter" (clôturées) et "Matrice" (en cours)
        // deviennent des liens accent soulignés — homogène avec la navigation
        // inter-pages. "Clore" et ✎ restent des CTAs button (actions
        // modifiant l'état, pas de la navigation).
        const linkStyle =
          'font-size:12px;color:var(--accent);text-decoration:underline;margin-right:10px';
        const actions = isClosed
          ? `<a href="#revision/${r.id}" style="${linkStyle}">${esc(t('pageProject.btn.consult'))}</a>`
          : `<a href="#matrix/${pid}" style="${linkStyle}" title="${esc(t('pageProject.btn.matrixTitle', { plural: t('vocabulary.risk_plural') }))}">${esc(t('pageProject.btn.matrix'))}</a>
             <button class="btn btn-secondary btn-sm" data-close-revision="${r.id}" style="margin-left:4px">${esc(t('pageProject.btn.closeRevision'))}</button>
             <button class="btn btn-secondary btn-icon" data-edit-revision="${r.id}" data-rev-name="${esc(r.name)}" data-rev-start="${esc(r.date_start)}" data-rev-end="${esc(r.date_end || '')}" title="${esc(t('pageProject.btn.editRevisionTitle'))}" style="margin-left:4px">✎</button>`;
        return `<tr data-rev-id="${r.id}">
          <td><a href="#revision/${r.id}" style="font-weight:600;color:var(--accent);text-decoration:underline">${esc(r.name)}</a></td>
          <td>${statusLabel}</td>
          <td style="font-size:12px;white-space:nowrap">${esc(fmtDate(r.date_start))}</td>
          <td style="font-size:12px;white-space:nowrap">${r.date_end ? esc(fmtDate(r.date_end)) : '—'}</td>
          <td style="white-space:nowrap">${actions}</td>
        </tr>`;
      })
      .join('');
    return `<div class="card">
      <div class="table-wrap"><table>
        <thead><tr>
          <th data-sort="name" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colName'))}${arrowFor('name')}</th>
          <th data-sort="status" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colStatus'))}${arrowFor('status')}</th>
          <th data-sort="date_start" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colStart'))}${arrowFor('date_start')}</th>
          <th data-sort="date_end" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colEnd'))}${arrowFor('date_end')}</th>
          <th></th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table></div>
    </div>`;
  };

  setContent(renderTable());

  const btnStart = document.getElementById('btn-start-revision');
  if (btnStart) btnStart.addEventListener('click', () => showStartRevisionModal(pid, project));
  const btnStart2 = document.getElementById('btn-start-revision-2');
  if (btnStart2) btnStart2.addEventListener('click', () => showStartRevisionModal(pid, project));

  const applyChecklistBtn = document.getElementById(
    'btn-apply-checklist',
  ) as HTMLButtonElement | null;
  applyChecklistBtn?.addEventListener('click', () => {
    void api.withButton(applyChecklistBtn, async () => {
      const templates = (await api.getTemplates()) as Array<{
        id: number;
        name: string;
        actions: unknown[];
      }>;
      if (!templates.length) {
        toast(t('pageProject.checklist.errMissing'), 'error');
        return;
      }
      const opts = templates
        .map(
          (tpl) =>
            `<option value="${tpl.id}">${t('pageProject.checklist.option', { name: tpl.name, n: tpl.actions.length })}</option>`,
        )
        .join('');
      openModal(
        t('pageProject.checklist.title'),
        `
      <div class="form-group"><label>${esc(t('pageProject.checklist.label'))}</label>
        <select id="modal-template-sel" style="width:100%"><option value="">${esc(t('pageProject.checklist.placeholder'))}</option>${opts}</select>
      </div>
      <div class="form-group" style="margin-top:10px">
        <label style="display:flex;align-items:flex-start;gap:8px;font-weight:400;cursor:pointer">
          <input type="checkbox" id="modal-template-notify" style="margin-top:3px">
          <span>
            ${esc(t('pageProject.checklist.notifyLabel'))}
            <span style="display:block;font-size:11px;color:var(--text3);margin-top:2px">${esc(t('pageProject.checklist.notifyHelp'))}</span>
          </span>
        </label>
      </div>
      <p style="font-size:12px;color:var(--text3);margin-top:8px">${esc(t('pageProject.checklist.help'))}</p>`,
        async () => {
          const sel = document.getElementById('modal-template-sel') as HTMLSelectElement | null;
          const tid = parseInt(sel?.value || '0');
          if (!tid) {
            toast(t('pageProject.checklist.errChoose'), 'error');
            return;
          }
          const notifyEl = document.getElementById(
            'modal-template-notify',
          ) as HTMLInputElement | null;
          const notify = !!notifyEl?.checked;
          const app = (await api.applyTemplate(pid, { template_id: tid, notify })) as {
            id: number;
          };
          closeModal();
          toast(t('pageProject.checklist.applied'));
          navigateTo('bascule/:appId', { appId: app.id });
        },
        t('pageProject.checklist.btnApply'),
      );
    });
  });

  const contentEl = document.getElementById('content');
  contentEl?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Tri par colonne : toggle ASC/DESC sur la même colonne, sinon switch + ASC.
    // Re-render uniquement la zone (les autres listeners restent valides via
    // délégation sur contentEl).
    const sortTh = target.closest<HTMLElement>('th[data-sort]');
    if (sortTh) {
      const key = (sortTh.dataset['sort'] || '') as typeof sortKey;
      if (sortKey === key) {
        sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey = key;
        sortDir = 'asc';
      }
      setContent(renderTable());
      return;
    }
    const openBtn = target.closest<HTMLElement>('[data-open-revision]');
    if (openBtn) {
      router.navigate('#revision/' + (openBtn.dataset['openRevision'] || ''));
      return;
    }
    const closeBtn = target.closest<HTMLElement>('[data-close-revision]');
    if (closeBtn) {
      openModal(
        t('pageProject.closeRev.title'),
        t('pageProject.closeRev.body'),
        async () => {
          try {
            await api.closeRevision(parseInt(closeBtn.dataset['closeRevision'] || '0'));
            closeModal();
            toast(t('pageProject.closeRev.toast'));
            const revs = (await api.getRevisions(pid)) as PageRevision[];
            renderProjectRevisionsPage(pid, project, revs);
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('pageProject.closeRev.btn'),
      );
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-revision]');
    if (editBtn) {
      const revId = parseInt(editBtn.dataset['editRevision'] || '0');
      showEditRevisionModal(
        revId,
        {
          name: editBtn.dataset['revName'] || '',
          date_start: editBtn.dataset['revStart'] || '',
          date_end: editBtn.dataset['revEnd'] || '',
        },
        async () => {
          const revs = (await api.getRevisions(pid)) as PageRevision[];
          renderProjectRevisionsPage(pid, project, revs);
        },
      );
      return;
    }
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROJECT EDIT (RefactoUX mai 2026)
// Remplace l'ancienne popup `showProjectModal` pour l'édition. Layout en 3
// blocs côte à côte (Général · Pilotes+Programmes · Comités) + un bloc
// "Risques" en pleine largeur. La création ad-hoc d'un projet conserve la
// modale dans page-projects.ts.
// ═══════════════════════════════════════════════════════════════════════════

interface ProjectEditUser {
  id: number;
  name: string;
  trigram?: string;
  is_team?: boolean;
}
interface ProjectEditProgram {
  id: number;
  code?: string | null;
  name: string;
  projects?: Array<{ id: number }>;
}

function renderProjectEditPage(
  pid: number,
  project: PageProject,
  stages: Array<{ id: number; name: string }>,
  riskLists: Array<{ id: number; name: string }>,
  users: ProjectEditUser[],
  committees: Array<{ id: number; code: string; name: string }>,
  programs: ProjectEditProgram[],
  revisions: PageRevision[],
): void {
  const indivUsers = (users || []).filter((u) => !u.is_team);
  const selectedPilots = new Set<number>(
    (project as PageProject & { pilots?: number[] }).pilots || [],
  );
  const initialCommitteePilots: Record<number, number> = {};
  for (const cp of ((
    project as PageProject & {
      committee_pilots?: Array<{ committee_id: number; user_id: number }>;
    }
  ).committee_pilots || []) as Array<{ committee_id: number; user_id: number }>) {
    initialCommitteePilots[cp.committee_id] = cp.user_id;
  }
  const projectPrograms = (programs || []).filter((pg) =>
    (pg.projects || []).some((pp) => pp.id === pid),
  );

  // Tri révisions (même logique que pages.projectRevisions). Tri par défaut
  // sur date_start desc — la révision en cours apparaît en haut.
  let sortKey: 'name' | 'status' | 'date_start' | 'date_end' = 'date_start';
  let sortDir: 'asc' | 'desc' = 'desc';

  // ── Header : breadcrumb + boutons Annuler/Enregistrer ──
  setHeader(
    `<a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('pageProject.breadcrumb.projects'))}</a> / ${esc(project.name)}`,
    `<button class="btn btn-neutral ml-auto" id="pm-btn-cancel" style="margin-right:6px">${esc(t('common.cancel'))}</button>` +
      `<button class="btn btn-primary" id="pm-btn-save">${esc(t('common.save'))}</button>`,
  );

  // ── Helpers HTML ──
  const stageOpts = stages
    .map(
      (s) =>
        `<option value="${esc(s.name)}" ${s.name === (project.current_stage || '') ? 'selected' : ''}>${esc(s.name)}</option>`,
    )
    .join('');
  const listOpts = riskLists
    .map(
      (l) =>
        `<option value="${l.id}" ${l.id === ((project as PageProject & { risk_list_id?: number }).risk_list_id || 0) ? 'selected' : ''}>${esc(l.name)}</option>`,
    )
    .join('');
  const userOpts = ['<option value="">—</option>']
    .concat(
      indivUsers.map(
        (u) =>
          `<option value="${u.id}">${esc(u.name || '')}${u.trigram ? ' (' + esc(u.trigram) + ')' : ''}</option>`,
      ),
    )
    .join('');

  const renderChips = (): string => {
    const chips = Array.from(selectedPilots)
      .map((id) => indivUsers.find((u) => u.id === id))
      .filter((u): u is ProjectEditUser => Boolean(u))
      .map(
        (u) =>
          `<span class="pm-pilot-chip" data-uid="${u.id}" title="${esc(u.name || '')}"
            style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);
                   font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">
            ${esc(u.trigram || u.name || '')}
            <button type="button" class="pm-pilot-remove" data-uid="${u.id}"
              style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
          </span>`,
      )
      .join('');
    // Si aucun pilote : même picto ⚠ que dans le dashboard pour signaler la
    // donnée manquante (helper cursor:help + title au survol).
    return (
      chips ||
      `<span style="color:var(--warn);font-size:14px;cursor:help" title="${esc(t('pageProjects.warnNoPilot'))}">⚠</span>
       <span style="color:var(--text3);font-size:11px;margin-left:6px">${esc(t('pageProjects.pilots.empty'))}</span>`
    );
  };

  const renderProgramsBlock = (): string => {
    if (!projectPrograms.length)
      return `<span style="color:var(--text3);font-size:11px;font-style:italic">${esc(t('pageProjects.programs.empty'))}</span>`;
    return projectPrograms
      .map(
        (pg) => `<div style="display:flex;align-items:center;gap:8px;font-size:12px;padding:3px 0">
          <span style="font-family:var(--font-mono);font-size:11px;font-weight:600;color:var(--text3);min-width:50px">${esc(pg.code || '—')}</span>
          <span>${esc(pg.name)}</span>
        </div>`,
      )
      .join('');
  };

  const renderCommitteesBlock = (): string => {
    if (!committees || !committees.length)
      return `<div style="color:var(--text3);font-size:11px;font-style:italic">${esc(t('pageProjects.committees.empty'))}</div>`;
    return committees
      .map((c) => {
        const sel = initialCommitteePilots[c.id] || '';
        const opts = userOpts.replace(`value="${sel}"`, `value="${sel}" selected`);
        const dotColor = sel ? 'var(--accent)' : 'var(--warn)';
        return `<div class="pm-cp-row" data-cid="${c.id}" style="display:grid;grid-template-columns:14px 1fr;gap:6px;align-items:center;padding:4px 0;border-bottom:1px solid var(--border)">
          <span class="pm-cp-dot" title="${sel ? '' : esc(t('pageProjects.pilots.notSet'))}" style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${dotColor}"></span>
          <div>
            <div style="font-size:12px;color:var(--text2);margin-bottom:2px"><span style="font-family:var(--font-mono);font-size:11px;font-weight:600;color:var(--text3);margin-right:6px">${esc(c.code)}</span>${esc(c.name)}</div>
            <select class="pm-cp" data-cid="${c.id}" style="font-size:12px;padding:4px 6px;border:1px solid var(--border2);border-radius:var(--radius);width:100%">${opts}</select>
          </div>
        </div>`;
      })
      .join('');
  };

  const arrowFor = (key: string): string =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const sortedRevisions = (): PageRevision[] => {
    return [...revisions].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = (a.name || '').localeCompare(b.name || '');
      else if (sortKey === 'status') cmp = Number(!!a.is_closed) - Number(!!b.is_closed);
      else if (sortKey === 'date_start')
        cmp = (a.date_start || '').localeCompare(b.date_start || '');
      else if (sortKey === 'date_end') {
        const ae = a.date_end || (a.is_closed ? '' : '￿');
        const be = b.date_end || (b.is_closed ? '' : '￿');
        cmp = ae.localeCompare(be);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  const renderRevisionsTable = (): string => {
    if (!revisions.length) {
      return `<div style="text-align:center;padding:20px 12px;color:var(--text3);font-size:13px">${esc(t('pageProject.noRevision'))}</div>`;
    }
    const linkStyle =
      'font-size:12px;color:var(--accent);text-decoration:underline;margin-right:10px';
    const rows = sortedRevisions()
      .map((r) => {
        const isClosed = !!r.is_closed;
        const statusLabel = isClosed
          ? `<span style="display:inline-block;padding:2px 8px;background:var(--bg3);color:var(--text2);border-radius:8px;font-size:11px">${esc(t('pageProject.rev.closed'))}</span>`
          : `<span style="display:inline-block;padding:2px 8px;background:var(--accent-bg);color:var(--accent);border-radius:8px;font-size:11px;font-weight:600">${esc(t('pageProject.history.inProgress'))}</span>`;
        const actions = isClosed
          ? `<a href="#revision/${r.id}" style="${linkStyle}">${esc(t('pageProject.btn.consult'))}</a>`
          : `<a href="#matrix/${pid}" style="${linkStyle}" title="${esc(t('pageProject.btn.matrixTitle', { plural: t('vocabulary.risk_plural') }))}">${esc(t('pageProject.btn.matrix'))}</a>
             <button class="btn btn-secondary btn-sm" data-close-revision="${r.id}" style="margin-left:4px">${esc(t('pageProject.btn.closeRevision'))}</button>
             <button class="btn btn-secondary btn-icon" data-edit-revision="${r.id}" data-rev-name="${esc(r.name)}" data-rev-start="${esc(r.date_start)}" data-rev-end="${esc(r.date_end || '')}" title="${esc(t('pageProject.btn.editRevisionTitle'))}" style="margin-left:4px">✎</button>`;
        return `<tr data-rev-id="${r.id}">
          <td><a href="#revision/${r.id}" style="font-weight:600;color:var(--accent);text-decoration:underline">${esc(r.name)}</a></td>
          <td>${statusLabel}</td>
          <td style="font-size:12px;white-space:nowrap">${esc(fmtDate(r.date_start))}</td>
          <td style="font-size:12px;white-space:nowrap">${r.date_end ? esc(fmtDate(r.date_end)) : '—'}</td>
          <td style="white-space:nowrap">${actions}</td>
        </tr>`;
      })
      .join('');
    return `<div class="table-wrap"><table>
      <thead><tr>
        <th data-sort="name" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colName'))}${arrowFor('name')}</th>
        <th data-sort="status" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colStatus'))}${arrowFor('status')}</th>
        <th data-sort="date_start" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colStart'))}${arrowFor('date_start')}</th>
        <th data-sort="date_end" style="cursor:pointer;user-select:none">${esc(t('pageProject.closedList.colEnd'))}${arrowFor('date_end')}</th>
        <th></th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
  };

  // Révision ouverte (au plus une par projet) — utilisée pour le CTA primaire
  // "Poursuivre la revue" qui ouvre directement #revision/:id. Si aucune
  // révision en cours, le CTA n'est pas affiché et "Démarrer une révision"
  // redevient l'action principale (mais reste secondaire visuellement —
  // l'utilisateur n'a pas forcément envie de créer une révision à chaque
  // visite, on évite de lui pousser un CTA primaire vert).
  const openRev = revisions.find((r) => !r.is_closed);

  const buildBody = (): string => `
    <!-- Layout 1/3 + 2/3 (mai 2026) :
         - Col gauche (1/3) : 3 cartes empilées — Général · Pilotes · Programmes
         - Col droite (2/3) : 1 carte Comités, avec sous-grille 2 sous-cols
         Le projet bouge peu côté Général/Pilotes ; donner plus de place aux
         comités évite que les selects de pilotes débordent. -->
    <div style="display:grid;grid-template-columns:1fr 2fr;gap:16px;align-items:start">

      <!-- Col gauche : 3 cartes empilées (spacing resserré mai 2026) -->
      <div style="display:flex;flex-direction:column;gap:10px">
        <!-- Bloc 1 : Général -->
        <div class="card" style="padding:10px 12px">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageProjects.tab.general'))}</h3>
          <!-- Code + lien KPI sur la même ligne (lien aligné à droite). -->
          <div class="form-group">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
              <label style="margin:0">${esc(t('pageProjects.field.code'))}</label>
              <a href="#stats/project-synthesis?proj=${pid}&locked=1"
                 style="font-size:12px;color:var(--accent);text-decoration:underline"
                 title="${esc(t('pageProjects.btnSynthTitle', { object: t('vocabulary.project') }))}">${esc(t('pageProjects.btnSynth'))}</a>
            </div>
            <input type="text" id="pm-code" class="text-mono w-full" maxlength="5" value="${esc(project.code || '')}"
              placeholder="${esc(t('pageProjects.field.codePlaceholder'))}" style="text-transform:uppercase">
          </div>
          <div class="form-group mt-2"><label>${esc(t('pageProjects.field.name'))}</label>
            <input type="text" id="pm-name" value="${esc(project.name)}" placeholder="${esc(t('pageProjects.field.namePlaceholder'))}" required></div>
          <div class="form-group mt-2"><label>${esc(t('pageProjects.field.stage'))}</label>
            <select id="pm-stage"><option value="">—</option>${stageOpts}</select></div>
          <div class="form-group mt-2"><label>${esc(t('pageProjects.field.riskList'))}</label>
            <select id="pm-risklist"><option value="">${esc(t('pageProjects.field.riskListNone'))}</option>${listOpts}</select></div>
        </div>

        <!-- Bloc 2 : Pilotes du projet -->
        <div class="card" style="padding:10px 12px">
          <h3 style="margin:0 0 6px 0;font-size:13px">${esc(t('pageProjects.tab.pilots', { object: t('vocabulary.project') }))}</h3>
          <div id="pm-pilots-block"
            title="${esc(t('pageProjects.pilots.btnAddTitle'))}"
            style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;display:flex;align-items:flex-start;gap:6px;cursor:pointer">
            <div id="pm-pilots-chips" style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px;align-items:center">${renderChips()}</div>
            <button type="button" id="pm-pilot-add-btn" class="btn btn-secondary btn-icon"
              style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start" title="${esc(t('pageProjects.pilots.btnAddTitle'))}">+</button>
          </div>
        </div>

        <!-- Bloc 3 : Programmes -->
        <div class="card" style="padding:10px 12px">
          <h3 style="margin:0 0 6px 0;font-size:13px">${esc(t('vocabulary.program_plural'))}</h3>
          <div id="pm-programs">${renderProgramsBlock()}</div>
        </div>
      </div>

      <!-- Col droite : Pilotes des comités sur 2 sous-colonnes -->
      <div class="card" style="padding:12px 14px">
        <h3 style="margin:0 0 10px 0;font-size:13px">${esc(t('pageProjects.tab.committees', { plural: t('vocabulary.committee_plural') }))}</h3>
        <div id="pm-committees" style="display:grid;grid-template-columns:1fr 1fr;column-gap:18px;row-gap:0">${renderCommitteesBlock()}</div>
      </div>
    </div>

    <!-- Bloc Risques pleine largeur -->
    <div class="card" style="padding:14px;margin-top:16px">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;flex-wrap:wrap">
        <h3 style="margin:0;font-size:14px">${esc(t('vocabulary.risk_plural'))}</h3>
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
          <a href="#evolution/${pid}" id="pm-link-evolution" style="color:var(--accent);text-decoration:underline;font-size:12px" title="${esc(t('pageProject.btn.evolutionTitle'))}">${esc(t('pageProject.btn.evolution'))}</a>
          ${
            openRev
              ? `<a href="#revision/${openRev.id}" class="btn btn-primary btn-sm" id="pm-btn-continue-review">${esc(t('pageProject.btn.continueReview'))}</a>`
              : ''
          }
          <button class="btn btn-secondary btn-sm" id="pm-btn-start-revision">${esc(t('pageProject.btn.startRevision'))}</button>
        </div>
      </div>
      <div id="pm-revisions">${renderRevisionsTable()}</div>
    </div>`;

  setContent(buildBody());

  // Garde "modifications non enregistrées" (audit UX 2026-06) : toute saisie
  // marque la page comme modifiée. Le routeur (clic nav) et beforeunload
  // consultent ce flag avant de perdre la saisie. Nettoyé au prochain dispatch.
  let _projEditDirty = false;
  const _markProjEditDirty = (): void => {
    _projEditDirty = true;
  };
  const _projEditContent = document.getElementById('content');
  _projEditContent?.addEventListener('input', _markProjEditDirty);
  _projEditContent?.addEventListener('change', _markProjEditDirty);
  setUnsavedGuard(() => _projEditDirty);

  // Mode US (mai 2026) : verrouille les CTA et combo liés aux risques.
  if (window.isSoloUser?.() && window.markLocked) {
    const evol = document.getElementById('pm-link-evolution');
    if (evol) window.markLocked(evol, 'solo');
    const startRev = document.getElementById('pm-btn-start-revision');
    if (startRev) window.markLocked(startRev, 'solo');
    const continueRev = document.getElementById('pm-btn-continue-review');
    if (continueRev) window.markLocked(continueRev, 'solo');
    const riskList = document.getElementById('pm-risklist');
    if (riskList) window.markLocked(riskList.parentElement || riskList, 'solo');
  }

  // ── Wiring ──
  document.getElementById('pm-code')?.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (target) target.value = target.value.toUpperCase();
  });

  // Pilote : clic n'importe où dans le bloc = clic sur "+". Le picker reste
  // celui du dropdown ancré sous le bouton.
  const refreshChips = (): void => {
    const el = document.getElementById('pm-pilots-chips');
    if (el) el.innerHTML = renderChips();
    document.querySelectorAll<HTMLElement>('.pm-pilot-remove').forEach((b) => {
      b.addEventListener('click', (ev: Event) => {
        ev.stopPropagation();
        selectedPilots.delete(parseInt(b.dataset['uid'] || '0'));
        _markProjEditDirty();
        refreshChips();
      });
    });
  };
  refreshChips();

  // Picker de pilotes factorisé dans utils/pilot-picker (dette #6a-1) ; on lie
  // ici le contexte de la page d'édition (users, set sélectionné, dirty, refresh).
  const openPicker = (anchor: HTMLElement): void =>
    openProjectPilotPicker({
      anchor,
      users: indivUsers,
      selected: selectedPilots,
      onChange: _markProjEditDirty,
      onClose: refreshChips,
    });

  // Clic sur le bouton "+" OU sur n'importe quelle zone du bloc pilotes
  // (hors chip déjà sélectionnée) → ouvre le picker. UX demandée mai 2026.
  document.getElementById('pm-pilot-add-btn')?.addEventListener('click', (ev: Event) => {
    ev.stopPropagation();
    openPicker(ev.currentTarget as HTMLElement);
  });
  document.getElementById('pm-pilots-block')?.addEventListener('click', (ev: Event) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    // Ignore les clics sur une puce ou son × (handlers déjà câblés).
    if (target.closest('.pm-pilot-chip') || target.closest('.pm-pilot-remove')) return;
    if (target.closest('#pm-pilot-add-btn')) return; // déjà géré
    const btn = document.getElementById('pm-pilot-add-btn') as HTMLElement | null;
    if (btn) openPicker(btn);
  });

  // Couleur des dots de comités au changement de pilote.
  document.querySelectorAll<HTMLSelectElement>('.pm-cp').forEach((sel) => {
    sel.addEventListener('change', () => {
      const dot = sel.closest<HTMLElement>('.pm-cp-row')?.querySelector<HTMLElement>('.pm-cp-dot');
      if (dot) {
        dot.style.background = sel.value ? 'var(--accent)' : 'var(--warn)';
        dot.title = sel.value ? '' : t('pageProjects.pilots.notSet');
      }
    });
  });

  // Save / Annuler.
  const doSave = async (): Promise<void> => {
    const codeEl = document.getElementById('pm-code') as HTMLInputElement | null;
    const nameEl = document.getElementById('pm-name') as HTMLInputElement | null;
    const codeVal = (codeEl?.value || '').trim().toUpperCase();
    const nameVal = nameEl?.value.trim() || '';
    if (!codeVal) {
      toast(t('pageProjects.errCode'), 'error');
      return;
    }
    if (!nameVal) {
      toast(t('pageProjects.errName'), 'error');
      return;
    }
    const stageEl = document.getElementById('pm-stage') as HTMLSelectElement | null;
    const listEl = document.getElementById('pm-risklist') as HTMLSelectElement | null;
    const pilots = Array.from(selectedPilots);
    const committee_pilots = Array.from(document.querySelectorAll<HTMLSelectElement>('.pm-cp'))
      .map((s) => ({
        committee_id: parseInt(s.dataset['cid'] || '0'),
        user_id: parseInt(s.value) || null,
      }))
      .filter((x): x is { committee_id: number; user_id: number } => x.user_id !== null);
    try {
      await api.updateProject(pid, {
        code: codeVal,
        name: nameVal,
        current_stage: stageEl?.value || null,
        risk_list_id: parseInt(listEl?.value || '0') || null,
        pilots,
        committee_pilots,
      });
      toast(t('pageProjects.toastEdit'));
      markLastModified('projects', pid);
      clearUnsavedGuard();
      window.location.hash = '#dashboard';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast(msg, 'error');
    }
  };
  document.getElementById('pm-btn-save')?.addEventListener('click', (e: Event) => {
    const btn = e.currentTarget as HTMLButtonElement;
    void api.withButton(btn, doSave);
  });
  document.getElementById('pm-btn-cancel')?.addEventListener('click', () => {
    void (async () => {
      if (!(await confirmLeaveIfDirty())) return;
      window.location.hash = '#dashboard';
    })();
  });

  // Démarrer une révision : réutilise la modale existante (open ou nouveau).
  document.getElementById('pm-btn-start-revision')?.addEventListener('click', () => {
    showStartRevisionModal(pid, project);
  });

  // Bloc Risques : délégation pour le tri colonne + clic-révision (consult/edit).
  // Reuse pattern de pages.projectRevisions : pas de re-render manuel — on
  // remplace juste le innerHTML du conteneur Risques après un tri/action.
  const revisionsContainer = document.getElementById('pm-revisions');
  revisionsContainer?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const sortTh = target.closest<HTMLElement>('th[data-sort]');
    if (sortTh) {
      const key = (sortTh.dataset['sort'] || '') as typeof sortKey;
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = 'asc';
      }
      revisionsContainer.innerHTML = renderRevisionsTable();
      return;
    }
    const closeBtn = target.closest<HTMLElement>('[data-close-revision]');
    if (closeBtn) {
      openModal(
        t('pageProject.closeRev.title'),
        t('pageProject.closeRev.body'),
        async () => {
          try {
            await api.closeRevision(parseInt(closeBtn.dataset['closeRevision'] || '0'));
            closeModal();
            toast(t('pageProject.closeRev.toast'));
            // Recharge la page complète pour rafraîchir l'état (revisions à jour).
            void pages.project?.({ id: String(pid) });
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('pageProject.closeRev.btn'),
      );
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-revision]');
    if (editBtn) {
      const revId = parseInt(editBtn.dataset['editRevision'] || '0');
      showEditRevisionModal(
        revId,
        {
          name: editBtn.dataset['revName'] || '',
          date_start: editBtn.dataset['revStart'] || '',
          date_end: editBtn.dataset['revEnd'] || '',
        },
        async () => {
          void pages.project?.({ id: String(pid) });
        },
      );
      return;
    }
  });
}

interface RevisionEditCurrent {
  name: string;
  date_start: string;
  date_end?: string | null;
}

function showStartRevisionModal(pid: number, project: PageProject | null): void {
  const today = todayStr();
  let _currentStage = project ? project.current_stage || '' : '';

  const renderBody = (): string => `
    <div class="form-group"><label>${esc(t('pageProject.startRev.name'))}</label>
      <input type="text" id="sr-name" placeholder="${esc(t('pageProject.startRev.namePlaceholder'))}" style="width:100%"></div>
    <div class="form-group" style="margin-top:10px"><label>${esc(t('pageProject.startRev.dateStart'))}</label>
      <input type="date" id="sr-date-start" value="${today}" max="${today}"></div>
    <div style="margin-top:12px;padding:10px 12px;background:var(--bg3);border-radius:var(--radius);border:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:10px">
      <div>
        <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--text3)">${esc(t('pageProject.startRev.stage'))}</div>
        <div id="sr-stage-display" style="font-size:13px;font-weight:500;margin-top:2px">${_currentStage ? esc(_currentStage) : `<span style="color:var(--text3)">${esc(t('pageProject.startRev.stageNone'))}</span>`}</div>
        <div style="font-size:11px;color:var(--text3);margin-top:2px">${esc(t('pageProject.startRev.stageHelp'))}</div>
      </div>
      <button type="button" id="sr-change-stage" class="btn btn-secondary btn-sm" style="flex-shrink:0">${esc(t('pageProject.startRev.btnChange'))}</button>
    </div>`;

  openModal(
    t('pageProject.startRev.title'),
    renderBody(),
    async () => {
      const nameEl = document.getElementById('sr-name') as HTMLInputElement | null;
      const name = (nameEl?.value || '').trim();
      if (!name) {
        toast(t('pageProject.startRev.errName'), 'error');
        return;
      }
      const startEl = document.getElementById('sr-date-start') as HTMLInputElement | null;
      const date_start = startEl?.value || today;
      try {
        const payload: Record<string, unknown> = { name, date_start };
        if (_currentStage !== (project?.current_stage || '')) {
          payload['current_stage'] = _currentStage;
        }
        const rev = (await api.startRevision(pid, payload)) as { id: number };
        closeModal();
        toast(t('pageProject.startRev.toast'));
        navigateTo('revision/:id', { id: rev.id });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    t('pageProject.startRev.btn'),
  );

  setTimeout(() => {
    document.getElementById('sr-change-stage')?.addEventListener('click', () => {
      void (async () => {
        // `void (async …)()` n'a pas de gestionnaire de rejet implicite : sans
        // ce `try`, une erreur ici produit une « unhandled promise rejection »
        // et le bouton reste sans effet, sans message. Même traitement que le
        // bloc « démarrer la révision » ci-dessus.
        try {
          const [stages, riskLists, projectFull] = await Promise.all([
            loadStages(),
            // Même repli que sur les deux chargements de page : un 403 sur
            // `/risk-lists` ne doit pas empêcher de changer de jalon.
            loadRiskLists().catch(() => []),
            api.getProject(pid),
          ]);
          await window.showProjectModal(
            projectFull as never,
            stages as never,
            riskLists as never,
            async (updated) => {
              if (project) project.current_stage = updated.current_stage || '';
              showStartRevisionModal(pid, project);
            },
          );
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      })();
    });
  }, 0);
}

function showEditRevisionModal(
  revId: number,
  current: RevisionEditCurrent,
  onSaved?: () => void | Promise<void>,
): void {
  const today = todayStr();
  const body = `
    <div class="form-group"><label>${esc(t('pageProject.editRev.name'))}</label>
      <input type="text" id="er-name" value="${esc(current.name)}" placeholder="${esc(t('pageProject.startRev.namePlaceholder'))}" style="width:100%"></div>
    <div class="form-row" style="margin-top:10px">
      <div class="form-group"><label>${esc(t('pageProject.editRev.dateStart'))}</label>
        <input type="date" id="er-date-start" value="${esc(current.date_start)}" max="${today}"></div>
      <div class="form-group"><label>${esc(t('pageProject.editRev.dateEnd'))}</label>
        <input type="date" id="er-date-end" value="${esc(current.date_end || '')}" max="${today}"></div>
    </div>`;
  openModal(
    t('pageProject.editRev.title'),
    body,
    async () => {
      const nameEl = document.getElementById('er-name') as HTMLInputElement | null;
      const name = (nameEl?.value || '').trim();
      if (!name) {
        toast(t('pageProject.startRev.errName'), 'error');
        return;
      }
      try {
        const startEl = document.getElementById('er-date-start') as HTMLInputElement | null;
        const endEl = document.getElementById('er-date-end') as HTMLInputElement | null;
        await api.updateRevision(revId, {
          name,
          date_start: startEl?.value || '',
          date_end: endEl?.value || null,
        });
        closeModal();
        toast(t('pageProject.editRev.toast'));
        if (onSaved) await onSaved();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    t('pageProject.editRev.btn'),
  );
}

interface RevisionFetchEntry {
  id: number;
  probability?: number;
  impact?: number;
  is_relevant?: boolean;
  owner_id?: number | null;
  actions: PageAction[];
  linked_actions?: PageAction[];
}

interface RevisionFetchPayload {
  revision: PageRevision;
  entries: Record<string, RevisionFetchEntry>;
  prev_entries?: Record<string, PagePrevEntry>;
}

pages.revision = definePage<'revision/:id'>(async ({ id }) => {
  const revId = id;
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('pageProject.revLoading'))}</div>`);
  try {
    const [revData, users, stages, actionStatuses, allProjects] = (await Promise.all([
      api.getRevision(revId),
      loadUsers(),
      loadStages(),
      loadActionStatuses(),
      api.getProjects(),
    ])) as unknown as [
      RevisionFetchPayload,
      PageUser[],
      PageStage[],
      PageActionStatus[],
      PageProjectMin[],
    ];
    const { revision, entries, prev_entries } = revData;
    const risks = (await api.getProjectRisks(revision.project_id)) as PageRisk[];
    const project = (await api.getProject(revision.project_id)) as PageProject;

    ps.project = project;
    ps.revision = revision;
    ps.allProjects = allProjects || [];
    ps.risks = risks;
    ps.users = users;
    ps.stages = stages;
    ps.actionStatuses = actionStatuses;
    ps.selectedStage = project.current_stage || stages[0]?.name || null;
    ps.expandedRiskId = null;
    ps.sortBy = 'severity';
    ps.sortDir = 'desc';
    ps.visibilityFilter = 'all';
    ps.showNonRelevant = true;
    // `ps` est un singleton de module partagé entre les pages : sans cette
    // remise à zéro, une case « escaladés seulement » cochée sur une autre
    // révision survivait à la navigation et masquait ici des risques ÉVALUÉS —
    // ce que la règle de population interdit précisément (2026-08-27). Les
    // filtres voisins étaient remis à zéro, celui-ci avait été oublié.
    ps.showEscalatedOnly = false;
    ps.stableOrder = risks.map((r) => r.id);
    ps.dirtyRisks = new Set();
    ps.dirtyActions = new Set();
    ps.newActions = [];
    ps.deletedActionIds = [];
    ps.newLinks = [];
    ps.deletedLinkIds = [];

    ps.prevEntries = prev_entries || {};
    const newEntries: Record<number, PageEntry> = {};
    for (const r of risks) {
      const e = entries[String(r.id)];
      newEntries[r.id] = {
        entryId: e ? e.id : null,
        probability: e ? (e.probability ?? 1) : 1,
        impact: e ? (e.impact ?? 1) : 1,
        is_relevant: e ? e.is_relevant !== false : true,
        owner_id: e ? (e.owner_id ?? null) : null,
        actions: e ? e.actions.map((a) => normalizeAction(a)) : [],
        linked_actions: e ? e.linked_actions || [] : [],
        linked_action_ids: e ? (e.linked_actions || []).map((a) => a.id ?? 0) : [],
      };
    }
    ps.entries = newEntries;

    ps._unloadGuard = (evt: BeforeUnloadEvent) => {
      if (
        ps.dirtyRisks?.size ||
        ps.newActions?.length ||
        ps.dirtyActions?.size ||
        ps.newLinks?.length ||
        ps.deletedActionIds?.length
      ) {
        evt.preventDefault();
        evt.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', ps._unloadGuard);
    registerPageDisposer(_removeUnloadGuard);

    renderProjectPage();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

function _removeUnloadGuard(): void {
  if (ps._unloadGuard) {
    window.removeEventListener('beforeunload', ps._unloadGuard);
    ps._unloadGuard = null;
  }
}

async function _confirmLeave(): Promise<boolean> {
  if (
    !ps.dirtyRisks ||
    (!ps.dirtyRisks.size &&
      !ps.newActions?.length &&
      !ps.dirtyActions?.size &&
      !ps.newLinks?.length &&
      !ps.deletedActionIds?.length)
  )
    return true;
  return new Promise<boolean>((resolve) => {
    openModal(
      t('pageProject.unsaved.title'),
      t('pageProject.unsaved.body'),
      () => {
        closeModal();
        resolve(true);
      },
      t('pageProject.unsaved.btn'),
    );
    const cancelBtn = document.getElementById('modal-cancel') as HTMLButtonElement | null;
    if (cancelBtn) {
      const orig = cancelBtn.onclick;
      cancelBtn.onclick = (ev) => {
        if (orig) orig.call(cancelBtn, ev);
        resolve(false);
      };
    }
  });
}

// ── ESM globals shim ─────────────────────────────────────────────────────
Object.assign(window, {
  ps,
  renderProjectRevisionsPage,
  showStartRevisionModal,
  showEditRevisionModal,
  _removeUnloadGuard,
  _confirmLeave,
});

export {};
